import { store } from '../store';
import { addSpeechRateAlert, addTranscript } from '../store/conversationSlice';
import { participantLinesMoved, participantMerged } from '../store/participantActions';
import { ParticipantMerge, parseParticipantLinesMove, parseParticipantMerge } from '../utils/participantLinesMoved';
import { config } from '../config';
import { LiveReconnectScheduler } from './liveReconnect';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { joinUrlPath } from '../utils/joinUrlPath';
import { participantIdOf } from '../utils/participantId';
import { parseParticipantRoleChange } from '../utils/participantRoleChange';
import { applyParticipantRoleChange } from './participantRoleChanges';
import { STANDALONE_JOIN_MODE, StandaloneJoinMode } from '../constants/watchSession';

/** Same keepalive as the desktop apps' session socket. */
const HEARTBEAT_INTERVAL_MS = 30_000;
/** The speech-rate default the services share, for a frame that carries no threshold. */
const DEFAULT_WPM_THRESHOLD = 190;

export interface StandaloneWatchTarget {
  sessionId: string;
  /**
   * `observer_<n>` from the join (the server treats the socket as read-only),
   * or a co-seller's `salesperson_<n>` (read-only too, except `leave_session`).
   */
  participantId: string;
  /** Observer unless the user joined with their mic. */
  role?: StandaloneJoinMode;
}

export interface StandaloneSessionEnded {
  sessionId: string;
  reason?: string;
}

/** Resolves false once the session has ended. Asked before every reconnect attempt. */
export type SessionLiveCheck = (sessionId: string) => Promise<boolean>;

type SessionEndedListener = (event: StandaloneSessionEnded) => void;
type ReconnectedListener = (sessionId: string) => void;

interface SessionFrameData {
  text?: string;
  speaker?: string;
  /** transcript_broadcast: which socket produced the line (C3), `salesperson` (a mic) or `customer`. */
  connectionSpeaker?: string | null;
  participantId?: string | number | null;
  speakerName?: string | null;
  timestamp?: string;
  name?: string | null;
  role?: string | null;
  message?: string;
  // speech_rate_alert
  words_per_minute?: number | null;
  wpm_threshold?: number;
  max_words?: number;
}

interface SessionFrame {
  type?: string;
  session_id?: string;
  reason?: string;
  // participant_reclassified (merge shape) and participant_lines_moved carry more fields; parsed defensively.
  data?: SessionFrameData & Record<string, unknown>;
}

/**
 * A watcher's socket on a standalone (desktop) call: `/ws/desktop` opened as
 * the `observer_<n>` participant the join returned, with the user's Firebase
 * token, exactly as the desktop apps' observer mode does (macOS
 * `WebSocketManager.startObserverMode`). The server admits any user of the
 * tenant as an observer and ignores everything it sends but `ping`, so this
 * socket sends nothing else. It receives the room's merged transcript
 * (`transcript_broadcast`), roster relabels and merges
 * (`participant_reclassified`), lines moved to a newcomer
 * (`participant_lines_moved`) and the end (`session_ended`), and applies the
 * relabels exactly as the desktop apps do. Cards and alerts come on the production,
 * landmine and competitor sockets, joined to the same room.
 *
 * A co-seller (joined with their mic, D6 2026-10-05) opens the same socket as
 * its `salesperson_<n>`: it skips the broadcast of its own mic lines (C3; it
 * shows them from the proxy), takes the speech-rate alert the service sends it
 * alone, and says `leave_session` before it closes. Everything else it sends
 * is ignored too: co-sellers can only leave (D3).
 *
 * Reconnects under the shared live policy as `standalone` (fresh token every
 * attempt, no limit). Two things the server cannot tell an observer: a reopen
 * is reported to `onReconnected` listeners (the page then restores the history
 * missed while down), and before each attempt `isSessionLive` is asked, because
 * the service refuses an observer's reconnect to an ended session at the
 * handshake rather than sending it `session_ended`.
 */
class StandaloneWatchSocket {
  private ws: WebSocket | null = null;
  private target: StandaloneWatchTarget | null = null;
  private isSessionLive: SessionLiveCheck | null = null;
  private hasOpenedTarget = false;
  /**
   * Merged voices (`from` -> the merge): a line still arriving under `from`
   * takes `into`'s identity at ingestion, as on the desktops (the server's own
   * alias covers it too; this covers lines already in flight).
   */
  private readonly mergedParticipants = new Map<string, ParticipantMerge>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private readonly endedListeners = new Set<SessionEndedListener>();
  private readonly reconnectedListeners = new Set<ReconnectedListener>();
  private readonly reconnect = new LiveReconnectScheduler('standalone', () => this.reconnectToTarget());

  async connect(target: StandaloneWatchTarget, isSessionLive?: SessionLiveCheck): Promise<void> {
    if (this.target?.sessionId !== target.sessionId) this.hasOpenedTarget = false;
    this.target = target;
    this.isSessionLive = isSessionLive ?? null;
    this.reconnect.activate();
    await this.open(target);
  }

  /**
   * A co-seller leaves: tell the server (`leave_session`, C3), which ends only
   * their part of the call. An observer sends nothing. The caller disconnects.
   */
  leaveSession(): void {
    if (this.target?.role !== STANDALONE_JOIN_MODE.salesperson) return;
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'leave_session' }));
  }

  /** The watcher or co-seller left, or the session is over: no more attempts. */
  disconnect(): void {
    this.reconnect.stop();
    this.stopHeartbeat();
    this.closeSocket();
    this.target = null;
    this.isSessionLive = null;
    this.hasOpenedTarget = false;
    this.mergedParticipants.clear();
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** The session ended (server `session_ended`, or found ended before a reconnect). */
  onSessionEnded(listener: SessionEndedListener): () => void {
    this.endedListeners.add(listener);
    return () => {
      this.endedListeners.delete(listener);
    };
  }

  /** The socket reopened after a drop (not its first open). */
  onReconnected(listener: ReconnectedListener): () => void {
    this.reconnectedListeners.add(listener);
    return () => {
      this.reconnectedListeners.delete(listener);
    };
  }

  private async open(target: StandaloneWatchTarget): Promise<void> {
    const { token } = await validateAndRefreshToken();

    // disconnect() ran, or another session replaced this one, during the fetch.
    if (!this.reconnect.isActive() || this.target !== target) return;

    if (!token) {
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('No Firebase token for the standalone watcher socket');
    }

    this.closeSocket();
    const query =
      `session_id=${encodeURIComponent(target.sessionId)}` +
      `&token=${encodeURIComponent(token)}` +
      `&participant_id=${encodeURIComponent(target.participantId)}`;
    const socket = new WebSocket(`${joinUrlPath(config.server.standaloneWsUrl, 'ws/desktop')}?${query}`);
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) return;
      this.reconnect.markConnected();
      this.startHeartbeat();
      const isReopen = this.hasOpenedTarget;
      this.hasOpenedTarget = true;
      console.info(`[StandaloneWatch] ${isReopen ? 'Reconnected to' : 'Watching'} session ${target.sessionId}`);
      if (isReopen) this.reconnectedListeners.forEach((listener) => listener(target.sessionId));
    };

    socket.onmessage = (event) => {
      if (this.ws !== socket) return;
      this.handleFrame(event.data, target);
    };

    socket.onerror = (event) => {
      console.warn('[StandaloneWatch] Socket error:', event);
    };

    socket.onclose = (event) => {
      // A socket this service replaced or closed on purpose reports nothing.
      if (this.ws !== socket) return;
      this.ws = null;
      this.stopHeartbeat();
      console.info(`[StandaloneWatch] Socket closed: ${event?.code} ${event?.reason ?? ''}`);
      // Any drop while watching is retried; nothing missed is replayed live.
      this.reconnect.scheduleReconnect();
    };
  }

  private async reconnectToTarget(): Promise<void> {
    const target = this.target;
    if (!target) throw new Error('The standalone watcher has no session to rejoin');
    if (this.isSessionLive && !(await this.isSessionLive(target.sessionId))) {
      if (this.target === target) this.endSession(target.sessionId, 'server');
      return;
    }
    await this.open(target);
  }

  private handleFrame(rawFrame: string, target: StandaloneWatchTarget): void {
    let frame: SessionFrame;
    try {
      frame = JSON.parse(rawFrame);
    } catch (error) {
      console.warn('[StandaloneWatch] Unreadable frame:', error);
      return;
    }

    switch (frame?.type) {
      case 'transcript_broadcast':
        this.addTranscriptLine(frame.data, target);
        return;
      case 'speech_rate_alert':
        this.applySpeechRateAlert(frame.data, target);
        return;
      case 'participant_reclassified':
        this.applyReclassification(frame.data);
        return;
      case 'participant_lines_moved':
        this.applyLinesMoved(frame.data);
        return;
      case 'session_ended':
        if (frame.session_id && frame.session_id !== target.sessionId) return;
        this.endSession(target.sessionId, frame.reason);
        return;
      case 'error':
        console.warn('[StandaloneWatch] Server error:', frame.data?.message);
        return;
      default:
        // connection_established, pong, participant_joined / participant_left
        // (participant_reclassified carries the roster change), speech_rate_update
        // (no live readout on the web); session_idle_* goes to the creator alone (D3).
        return;
    }
  }

  /**
   * The room's merged transcript. C3's joiner rule: every line except the
   * viewer's own mic lines (`participantId` is theirs and it came from a
   * salesperson mic), which a co-seller already shows from the proxy. A line
   * of theirs that came off the owner's customer stream still shows.
   */
  private addTranscriptLine(data: SessionFrameData | undefined, target: StandaloneWatchTarget): void {
    if (!data?.text?.trim()) return;
    const participantId =
      data.participantId !== undefined && data.participantId !== null ? String(data.participantId) : undefined;
    const producedBy = data.connectionSpeaker ?? data.speaker;
    if (participantId === target.participantId && producedBy === 'salesperson') return;
    const merge = participantId ? this.mergedParticipants.get(participantId) : undefined;
    store.dispatch(
      addTranscript({
        text: data.text,
        speaker: merge?.intoRole ?? (data.speaker === 'salesperson' ? 'salesperson' : 'customer'),
        participantId: merge?.intoParticipantId ?? participantId,
        speakerName: merge ? merge.intoName ?? data.speakerName ?? undefined : data.speakerName || undefined,
        timestamp: data.timestamp || new Date().toISOString(),
      })
    );
  }

  /**
   * A roster change from the creator or the server: in place (`participantId`
   * renamed or re-roled: the roster row, their lines, their concern cards
   * (dropped for a salesperson) and their alerts, exactly as a bot call's
   * decision, utils/participantRoleChange R2) or a merge (`fromParticipantId`
   * into `intoParticipantId`, with `intoRole`: fold `from`'s lines into `into`).
   */
  private applyReclassification(data?: SessionFrame['data']): void {
    const merge = parseParticipantMerge(data);
    if (merge) {
      this.mergedParticipants.set(merge.fromParticipantId, merge);
      store.dispatch(participantMerged(merge));
      return;
    }
    const change = parseParticipantRoleChange(data);
    if (change) applyParticipantRoleChange(change);
  }

  /**
   * "Speaking too fast" for a co-seller: the service sends it to that
   * co-seller's socket alone, so it is theirs even when it names nobody. An
   * observer never gets one (it is the speaker's), and never shows one.
   */
  private applySpeechRateAlert(data: SessionFrameData | undefined, target: StandaloneWatchTarget): void {
    if (target.role !== STANDALONE_JOIN_MODE.salesperson) return;
    store.dispatch(
      addSpeechRateAlert({
        words_per_minute: data?.words_per_minute ?? 0,
        wpm_threshold: data?.wpm_threshold ?? data?.max_words ?? DEFAULT_WPM_THRESHOLD,
        timestamp: new Date().toISOString(),
        participantId: participantIdOf(data?.participantId) ?? target.participantId,
      })
    );
  }

  /** Lines AWS put under the wrong voice move to the newcomer, with the cards they raised. Silent. */
  private applyLinesMoved(data?: SessionFrame['data']): void {
    const move = parseParticipantLinesMove(data);
    if (!move) {
      console.warn('[StandaloneWatch] Ignoring a malformed participant_lines_moved');
      return;
    }
    store.dispatch(participantLinesMoved(move));
  }

  private endSession(sessionId: string, reason?: string): void {
    console.info(`[StandaloneWatch] Session ${sessionId} ended (${reason ?? 'no reason'})`);
    this.disconnect();
    this.endedListeners.forEach((listener) => {
      try {
        listener({ sessionId, reason });
      } catch (error) {
        console.error('[StandaloneWatch] session_ended listener failed:', error);
      }
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'ping' }));
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (!this.heartbeatTimer) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private closeSocket(): void {
    const socket = this.ws;
    const reason = this.target?.role === STANDALONE_JOIN_MODE.salesperson ? 'Co-seller left' : 'Watcher left';
    this.ws = null;
    socket?.close(1000, reason);
  }
}

export const standaloneWatchSocket = new StandaloneWatchSocket();
